import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import BridgeSection from './BridgeSection';
import BridgeSuggestionRow from './BridgeSuggestionRow';
import Skeleton from '../../../../ui/Skeleton';
import { useDeckSuggestions } from '../../catalog/useDeckSuggestions';
import { useSuggestionSettings } from '../../catalog/useSuggestionSettings';
import './CommandBridge.css';

/**
 * Vorschläge-Sektion der Brücke — das Schaufenster der Vorschlags-Maschine. Teilt sich
 * die Anfrage mit der Erstansicht des Katalog-Reiters über denselben Query-Key
 * (`useDeckSuggestions(deckId, {})`, siehe `catalog/useDeckSuggestions.js`) — eine
 * Invalidierung (z.B. nach einem Karten-Add) aktualisiert beide. Das gilt nur mit
 * denselben Start-Farben wie der Reiter (`initialColorIdentity`), sonst hätte die Brücke
 * einen eigenen Key.
 *
 * Der Wert-Slot des Kopfs bleibt leer; rechts steht „alle ansehen →" als Textlink im
 * `actions`-Slot (Geschwister des Toggles, kein Knopf im Knopf) — der Weg in den Katalog ist
 * damit auch zugeklappt sichtbar und lesbar, nicht nur ein Icon. Den Namen der Top-Karte
 * zeigt die Zeile selbst; im Kopf würde er den Link verdrängen.
 *
 * Aufgeklappt: genau EINE Karte als Querzeile (`BridgeSuggestionRow`) — Miniatur, Name,
 * Begründungs-Chips, Plus zum Hinzufügen. Kein ✕: „nicht vorschlagen" verwaltet man im
 * Reiter, wo auch der Weg zurück steht.
 *
 * Leerzustand (kein Profil, keine Treffer, oder Themen-Daten fehlen serverseitig):
 * neutrale Zeile ohne Auf/Zu — Klick öffnet direkt den Katalog (Muster aus dem
 * `BridgeSection`-Kopf: „Zeile ohne Inhalt nutzt `onToggle` für die Aktion, eigenes
 * Richtungssymbol im `value`", wie bei `WantlistRow`).
 */
export default function SuggestionSection({ deckId, open, onToggle, onAdd, onOpenCatalog, onOpenCard, initialColorIdentity }) {
  const { profile, entries, available, status } = useDeckSuggestions(
    deckId,
    initialColorIdentity?.length ? { filters: { colors: initialColorIdentity } } : {},
  );
  const { muteTag } = useSuggestionSettings(deckId);
  const [addingId, setAddingId] = useState(null);

  const leer = status === 'success' && (available === false || profile.length === 0 || entries.length === 0);

  if (leer) {
    return (
      <BridgeSection
        label="Vorschläge"
        value={<ArrowRight size={13} aria-hidden />}
        onToggle={onOpenCatalog}
      />
    );
  }

  const top = entries[0] ?? null;

  const handleAdd = async (entry) => {
    if (addingId) return;
    setAddingId(entry.id);
    try {
      await onAdd(entry);
    } finally {
      setAddingId(null);
    }
  };

  return (
    <BridgeSection
      label="Vorschläge"
      open={open}
      onToggle={onToggle}
      actions={(
        <button type="button" className="bridge-suggestions__all" aria-label="Alle Vorschläge ansehen" onClick={onOpenCatalog}>
          alle ansehen <ArrowRight size={13} aria-hidden />
        </button>
      )}
    >
      {status === 'pending' ? (
        <Skeleton height="118px" radius="md" label="Vorschläge werden geladen" />
      ) : status === 'error' ? (
        <p className="bridge-suggestions__hint">Vorschläge konnten nicht geladen werden.</p>
      ) : top ? (
        <BridgeSuggestionRow entry={top} adding={addingId === top.id} onAdd={handleAdd} onOpen={onOpenCard} onMuteTag={muteTag} />
      ) : null}
    </BridgeSection>
  );
}
